/**
 * @author Ka Wing Chin <kawing.chin@takeaway.com>
 * @class Takeaway.Voucher
 */

import Utils from '../lib/utils'

export default class Voucher {
  constructor (obj) {
    if (obj !== undefined) {
      for (let prop in obj) {
        this[prop] = obj[prop]
      }
    }
  }

  /**
   * @enum Voucher types
   * @type {Readonly<{Credit: number, Discount: number}>}
   */
  static Type = Object.freeze({
    Credit: 1,
    Discount: 2
  })

  voucherName
  description
  code
  type
  /**
   * Only in combination with Voucher.Type.Credit
   */
  initialCredit
  /**
   * Only in combination with Voucher.Type.Credit
   */
  currentCredit
  /**
   * Discount amount
   * Only in combination with Voucher.Type.Discount
   */
  amount
  /**
   * Discount percentage
   * Only in combination with Voucher.Type.Discount
   */
  percentage
  /**
   * Minimum requirement of the costs
   */
  minimumOrderAmount
  /**
   * Id of the required product
   */
  productId
  productName

  /**
   * Discount price after the calculation
   */
  discount = 0
  /**
   * How much the customer still needs to pay
   */
  remainingPayment = 0
  /**
   * The total price which the voucher is calculated on
   */
  basedOnTotalPrice = 0

  /**
   * Calculate voucher discount.
   * @param {float} price The price the voucher discount will be calculated from.
   */
  calculateVoucherDiscount (price) {
    this.discount = 0
    this.basedOnTotalPrice = price
    switch (Number(this.type)) {
      case Voucher.Type.Credit:
        if (this.currentCredit < price) {
          this.discount = this.currentCredit
        } else {
          this.discount = price
        }
        break
      case Voucher.Type.Discount:
        if (this.amount > 0) {
          this.discount = this.amount
        } else {
          this.discount = Utils.toMoney(((price * this.percentage) / 100), 2)
        }
        break
      default:
        break
    }

    this.remainingPayment = price - this.discount
    // if the discount is larger than the price, it will result into negative number.
    // the discount will be become the same as the price and the remaining payment will become 0.
    if (this.remainingPayment < 0) {
      this.discount = price
      this.remainingPayment = 0
    }
    return this.discount
  }

  /**
   * Validate if the price meets the voucher's minimum requirement.
   * @param {float} price The price you want to validate on.
   */
  validateMinimumAmount (price) {
    return price >= this.minimumOrderAmount
  }

  /**
   * Validate if the products in the baskets meets the voucher's product requirement.
   * @param {Array<Product>} products that are added to basket.
   */
  validateVoucherProduct (products) {
    if (this.productId === false) {
      return true
    }

    if (this.productId !== false) {
      for (const x in products) {
        const product = products[x]
        if (product.isDisabled) {
          continue
        }

        if (this.productId === product.id) {
          return true
        }
      }
    }
    return false
  }
}
